import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import Link from '@/routing/Link'
import { Gift, Loader2, ShieldAlert } from '@digital-twins/design-system/icons'
import { authApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { SocialLogin } from '@/components/auth/SocialLogin'
import { setReferralCookies, getReferralCookies } from '@/lib/utils/referral-cookie'
import { REFERRAL_BONUS_CREDITS } from '@/components/referral/constants'

export default function RegisterPage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteOrgName, setInviteOrgName] = useState('')
  const [inviteExpired, setInviteExpired] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)

  const inviteParam = searchParams.get('invite') || ''
  const orgIdParam = searchParams.get('orgId') || ''
  const emailParam = searchParams.get('email') || ''
  const refParam = searchParams.get('ref') || ''
  const codeParam = searchParams.get('code') || ''
  const returnTo = searchParams.get('returnTo')
  const isInvite = !!inviteParam
  const isLegacyOrgInvite = !!orgIdParam && !inviteParam
  const isReferral = !!refParam && !isInvite
  const { referralCode: existingReferralCookie } = getReferralCookies()
  const showSso = (!!refParam || !!existingReferralCookie) && !isInvite
  // Lock email when arriving from an audience invite accept flow.
  const isAudienceInviteReturn = !!emailParam && !!returnTo && returnTo.includes('/audiences/invites/')
  const signInParams = new URLSearchParams()
  if (email) signInParams.set('email', email)
  if (returnTo) signInParams.set('returnTo', returnTo)
  const signInHref = signInParams.toString()
    ? `/login?${signInParams.toString()}`
    : '/login'

  useEffect(() => {
    if (emailParam) setEmail(emailParam)
  }, [emailParam])

  useEffect(() => {
    if (refParam) {
      setReferralCookies(refParam, 'referral_link')
    }
  }, [refParam])

  useEffect(() => {
    if (!inviteParam) return
    setInviteLoading(true)
    authApi.getInviteInfo(inviteParam)
      .then((info) => {
        setInviteEmail(info.email)
        setEmail(info.email)
        setInviteOrgName(info.organizationName)
        setInviteExpired(info.expired)
      })
      .catch(() => {
        setError(t('register.errors.invalidInvite'))
      })
      .finally(() => setInviteLoading(false))
  }, [inviteParam])

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await authApi.checkEmail(email)

      const params = new URLSearchParams()
      params.set('email', email)
      if (inviteParam) params.set('invite', inviteParam)
      if (codeParam) params.set('code', codeParam)
      if (returnTo) params.set('returnTo', returnTo)

      router.push(`/register/complete?${params.toString()}`)
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError(t('register.errors.emailTaken'))
      } else {
        setError(err.response?.data?.message || t('register.errors.generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex w-full flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-1">
        <Heading level={1} size="3xl" className="text-center font-display font-normal tracking-tight">
          {isInvite && inviteOrgName
            ? t('register.joinOrg', { org: inviteOrgName })
            : t('register.title')}
        </Heading>
        <Text as="p" size="sm" color="muted" className="text-center">
          {t('register.subtitle')}
        </Text>
      </div>

      {isReferral && (
        <div className="rounded-xl border border-primary/15 bg-primary/8 p-4 text-sm">
          <div className="flex items-start gap-3">
            <Gift className="mt-0.5 size-5 shrink-0 text-primary" />
            <div>
              <p className="font-display text-[16px] font-normal text-foreground">
                {t('register.referralTitle')}
              </p>
              <Text as="p" size="sm" color="muted" className="mt-1">
                {t('register.referralBody', { credits: REFERRAL_BONUS_CREDITS })}
              </Text>
            </div>
          </div>
        </div>
      )}

      {isInvite && inviteExpired && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-medium text-foreground">{t('register.inviteExpiredTitle')}</p>
              <Text as="p" size="sm" color="muted" className="mt-1">
                {t('register.inviteExpiredBody')}
              </Text>
            </div>
          </div>
        </div>
      )}

      {isLegacyOrgInvite && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-medium text-foreground">{t('register.legacyInviteTitle')}</p>
              <Text as="p" size="sm" color="muted" className="mt-1">
                {t('register.legacyInviteBody')}
              </Text>
            </div>
          </div>
        </div>
      )}

      {showSso && <SocialLogin />}

      <form onSubmit={handleContinue} className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('email', { ns: 'common' })}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('emailPlaceholder')}
            readOnly={!!inviteEmail || isAudienceInviteReturn}
            className={(inviteEmail || isAudienceInviteReturn) ? 'cursor-not-allowed bg-muted/60' : ''}
            required
            autoComplete="email"
          />
          {inviteEmail && (
            <Text as="p" size="xs" color="muted">
              {t('register.inviteEmailHint', { org: inviteOrgName || t('register.theTeam') })}
            </Text>
          )}
          {isAudienceInviteReturn && !inviteEmail && (
            <Text as="p" size="xs" color="muted">
              {t('register.audienceInviteHint')}
            </Text>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" variant="cta" disabled={loading || inviteLoading || inviteExpired} className="w-full gap-2">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('register.checking')}
            </>
          ) : (
            t('continue', { ns: 'common' })
          )}
        </Button>

        {inviteEmail && (
          <Text as="p" size="sm" color="muted" className="text-center">
            {t('register.differentEmail')}{' '}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-sm"
              onClick={() => {
                setInviteEmail('')
                setEmail('')
              }}
            >
              {t('register.registerWithoutJoining', { org: inviteOrgName || t('register.theTeam') })}
            </Button>
          </Text>
        )}
      </form>

      <Text as="p" size="sm" color="muted" className="text-center">
        {t('register.haveAccount')}{' '}
        <Button variant="link" size="sm" className="h-auto p-0 text-sm" asChild>
          <Link href={signInHref}>{t('register.signIn')}</Link>
        </Button>
      </Text>
    </div>
  )
}
